import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import StoreMetrics from '../../components/StoreMetrics';
import ProPaywall from '@/components/ProPaywall';
import { 
  Store, Plus, Edit, Trash2, Eye, Package, ShoppingBag, 
  Image as ImageIcon, ExternalLink, AlertCircle, CheckCircle2,
  Crown, Loader2, Upload, X, Tag, DollarSign, Settings,
  Palette, Phone, Mail, Globe, Facebook, Instagram, MessageSquare,
  Languages, Share2, Copy, Check, Download, FileSpreadsheet
} from 'lucide-react';
import { translateProduct } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { value: 'telas', label: 'Telas' },
  { value: 'ropa', label: 'Ropa' },
  { value: 'accesorios', label: 'Accesorios / Avíos' },
  { value: 'servicios', label: 'Servicios' }
];

const PLAN_LIMITS = {
  free: { max_products: 5, price_visible: false },
  pro: { max_products: -1, price_visible: true }
};

const COLOR_PRESETS = [
  { name: 'Esmeralda', primary: '#059669', secondary: '#10B981' },
  { name: 'Azul', primary: '#2563EB', secondary: '#3B82F6' },
  { name: 'Púrpura', primary: '#7C3AED', secondary: '#8B5CF6' },
  { name: 'Rosa', primary: '#DB2777', secondary: '#EC4899' },
  { name: 'Naranja', primary: '#EA580C', secondary: '#F97316' },
  { name: 'Rojo', primary: '#DC2626', secondary: '#EF4444' },
  { name: 'Slate', primary: '#475569', secondary: '#64748B' },
];

// Image Upload Component
const ImageUploader = ({ value, onChange, label, aspectRatio = 'aspect-video' }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        credentials: 'include',  // Use cookies for auth
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        onChange(API_URL + data.url);
        toast.success('Imagen subida');
      } else {
        toast.error('Error al subir imagen');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div 
        className={`${aspectRatio} bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-400 transition-colors cursor-pointer overflow-hidden relative group`}
        onClick={() => fileInputRef.current?.click()}
      >
        {value ? (
          <>
            <img src={value} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-sm font-medium">Cambiar imagen</span>
            </div>
            <button 
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
            {uploading ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <>
                <Upload className="w-8 h-8 mb-2" />
                <span className="text-sm">Click para subir</span>
              </>
            )}
          </div>
        )}
      </div>
      <input 
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
};

// Multi Image Uploader
const MultiImageUploader = ({ images, onChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    
    const newImages = [...images];
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Imagen muy grande (máx 5MB)');
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          const fullUrl = API_URL + data.url;
          newImages.push(fullUrl);
        } else {
          console.error('Upload failed:', res.status);
          toast.error('Error al subir imagen');
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error('Error de conexión al subir');
      }
    }
    
    // Reset file input to allow re-uploading same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    onChange(newImages);
    setUploading(false);
    if (newImages.length > images.length) {
      toast.success('Imágenes subidas');
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };

  return (
    <div className="space-y-2">
      <Label>Imágenes del producto</Label>
      <div className="grid grid-cols-3 gap-2">
        {images.map((img, i) => (
          <div key={i} className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group">
            <img src={img} alt="" className="w-full h-full object-cover" />
            <button 
              type="button"
              onClick={() => removeImage(i)}
              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {images.length < 6 && (
          <div 
            data-testid="upload-image-button"
            className="aspect-square bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-400 cursor-pointer flex items-center justify-center"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            ) : (
              <Plus className="w-6 h-6 text-slate-400" />
            )}
          </div>
        )}
      </div>
      <input 
        ref={fileInputRef}
        data-testid="product-image-input"
        type="file"
        accept="image/*"
        multiple
        onChange={handleUpload}
        className="hidden"
      />
      <p className="text-xs text-slate-500">Máximo 6 imágenes, 5MB cada una</p>
    </div>
  );
};

const MyStorePage = () => {
  const { user } = useAuth();
  const { isUSA, formatPrice } = useLocale();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [storeOrders, setStoreOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateStore, setShowCreateStore] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showCustomizeDialog, setShowCustomizeDialog] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('');
  const [terms, setTerms] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [copied, setCopied] = useState(false);

  const [storeForm, setStoreForm] = useState({
    store_name: '',
    description: '',
    category: '',
    logo_url: '',
    cover_url: '',
    accepted_terms: false
  });

  const [customizeForm, setCustomizeForm] = useState({
    store_name: '',
    slug: '',
    description: '',
    logo_url: '',
    cover_url: '',
    primary_color: '#059669',
    secondary_color: '#10B981',
    banner_text: '',
    contact_email: '',
    contact_phone: '',
    contact_whatsapp: '',
    social_facebook: '',
    social_instagram: '',
    social_website: ''
  });

  const [productForm, setProductForm] = useState({
    name: '',
    name_en: '',
    category: '',
    subcategory: '',
    description: '',
    description_en: '',
    price: '',
    currency: isUSA ? 'USD' : 'MXN',
    hide_price: false,
    images: [],
    moq: '',
    unit: 'Mt',
    stock: '',
    tags: '',
    publish_region: 'MX',
    variants: { color: [], talla: [], modelo: [] }
  });
  const [newVariant, setNewVariant] = useState({ type: 'color', value: '' });
  const [translating, setTranslating] = useState(false);

  // Unit options based on category
  const getUnitOptions = () => {
    if (productForm.category === 'telas') {
      return [
        { value: 'Mt', label: isUSA ? 'Meters (Mt)' : 'Metros (Mt)' },
        { value: 'Yd', label: isUSA ? 'Yards (Yd)' : 'Yardas (Yd)' },
        { value: 'Kg', label: isUSA ? 'Kilograms (Kg)' : 'Kilogramos (Kg)' }
      ];
    } else {
      return [
        { value: 'pzas', label: isUSA ? 'Units' : 'Piezas (pzas)' }
      ];
    }
  };

  const showEnglishFields = productForm.publish_region === 'US' || productForm.publish_region === 'BOTH';

  const handleTranslate = async () => {
    if (!productForm.name && !productForm.description) {
      toast.error(isUSA ? 'Add name and description first' : 'Agrega nombre y descripción primero');
      return;
    }
    setTranslating(true);
    try {
      const result = await translateProduct(productForm.name, productForm.description);
      setProductForm(prev => ({
        ...prev,
        name_en: result.name_en || prev.name_en,
        description_en: result.description_en || prev.description_en
      }));
      toast.success(isUSA ? 'Translation completed!' : '¡Traducción completada!');
    } catch (error) {
      toast.error(isUSA ? 'Translation failed' : 'Error al traducir');
    } finally {
      setTranslating(false);
    }
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [storeRes, productsRes, leadsRes, termsRes, ordersRes] = await Promise.all([
        fetch(`${API_URL}/api/stores/my`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/stores/my/products`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/stores/my/leads`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/stores/terms?lang=${isUSA ? 'en' : 'es'}`),
        fetch(`${API_URL}/api/stores/my/orders`, { headers: getAuthHeaders() })
      ]);

      if (storeRes.ok) {
        const storeData = await storeRes.json();
        setStore(storeData);
        if (storeData) {
          setCustomizeForm({
            store_name: storeData.store_name || '',
            slug: storeData.slug || '',
            description: storeData.description || '',
            logo_url: storeData.logo_url || '',
            cover_url: storeData.cover_url || '',
            primary_color: storeData.primary_color || '#059669',
            secondary_color: storeData.secondary_color || '#10B981',
            banner_text: storeData.banner_text || '',
            contact_email: storeData.contact_email || '',
            contact_phone: storeData.contact_phone || '',
            contact_whatsapp: storeData.contact_whatsapp || '',
            social_facebook: storeData.social_facebook || '',
            social_instagram: storeData.social_instagram || '',
            social_website: storeData.social_website || ''
          });
        }
      }
      if (productsRes.ok) setProducts(await productsRes.json());
      if (leadsRes.ok) setLeads(await leadsRes.json());
      if (ordersRes.ok) setStoreOrders(await ordersRes.json());
      if (termsRes.ok) {
        const termsData = await termsRes.json();
        setTerms(termsData.terms);
      }
    } catch (error) {
      console.error('Error fetching store data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isUSA]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateStore = async () => {
    if (!storeForm.store_name || !storeForm.category) {
      toast.error(isUSA ? 'Name and category are required' : 'Nombre y categoría son requeridos');
      return;
    }
    if (!storeForm.accepted_terms) {
      toast.error(isUSA ? 'You must accept the terms and conditions' : 'Debes aceptar los términos y condiciones');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/stores`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(storeForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(isUSA ? 'Store created successfully!' : '¡Tienda creada exitosamente!');
        setShowCreateStore(false);
        fetchData();
      } else if (res.status === 403) {
        // User needs Pro plan - redirect to subscription page
        setShowCreateStore(false);
        toast.info(isUSA 
          ? 'Activate your Pro plan to create your store' 
          : 'Activa tu plan Pro para crear tu tienda');
        navigate('/dashboard/subscription');
      } else {
        const data = await res.json();
        toast.error(data.detail || (isUSA ? 'Error creating store' : 'Error al crear tienda'));
      }
    } catch (error) {
      console.error('Error creating store:', error);
      toast.error(isUSA ? 'Connection error. Please try again.' : 'Error de conexión. Por favor intenta de nuevo.');
    } finally {
      setProcessing(false);
    }
  };

  const getStoreUrl = () => {
    const baseUrl = 'https://texhub.com.mx';
    return `${baseUrl}/tienda/${store?.slug}`;
  };

  const handleShareStore = async () => {
    const storeUrl = getStoreUrl();
    const shareData = {
      title: store?.store_name || 'Mi Tienda TexHub',
      text: isUSA 
        ? `Check out my store ${store?.store_name} on TexHub!` 
        : `¡Conoce mi tienda ${store?.store_name} en TexHub!`,
      url: storeUrl
    };

    // Try native share first (mobile)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Share failed:', err);
        }
      }
    }

    // Fallback: copy to clipboard
    handleCopyStoreLink();
  };

  const handleCopyStoreLink = () => {
    const storeUrl = getStoreUrl();
    navigator.clipboard.writeText(storeUrl).then(() => {
      setCopied(true);
      toast.success(isUSA ? 'Link copied! Share it with your customers.' : '¡Enlace copiado! Compártelo con tus clientes.');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error(isUSA ? 'Could not copy link' : 'No se pudo copiar el enlace');
    });
  };

  const handleSaveCustomization = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/stores/my`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(customizeForm)
      });
      if (res.ok) {
        toast.success(isUSA ? 'Store updated' : 'Tienda actualizada');
        setShowCustomizeDialog(false);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || (isUSA ? 'Error saving' : 'Error al guardar'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error saving' : 'Error al guardar');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.category || !productForm.description) {
      toast.error(isUSA ? 'Name, category and description are required' : 'Nombre, categoría y descripción son requeridos');
      return;
    }

    setProcessing(true);
    try {
      // Clean up variants - only include non-empty arrays
      const cleanVariants = {};
      if (productForm.variants) {
        if (productForm.variants.color?.length > 0) cleanVariants.color = productForm.variants.color;
        if (productForm.variants.talla?.length > 0) cleanVariants.talla = productForm.variants.talla;
        if (productForm.variants.modelo?.length > 0) cleanVariants.modelo = productForm.variants.modelo;
      }

      const payload = {
        ...productForm,
        price: productForm.price ? parseFloat(productForm.price) : null,
        moq: productForm.moq ? parseInt(productForm.moq) : null,
        stock: productForm.stock ? parseInt(productForm.stock) : null,
        tags: productForm.tags ? productForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        images: productForm.images || [],
        name_en: productForm.name_en || '',
        description_en: productForm.description_en || '',
        publish_region: productForm.publish_region || 'MX',
        unit: productForm.unit || 'Mt',
        variants: Object.keys(cleanVariants).length > 0 ? cleanVariants : null
      };

      const url = editingProduct 
        ? `${API_URL}/api/stores/my/products/${editingProduct.product_id}`
        : `${API_URL}/api/stores/my/products`;
      
      const res = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(editingProduct 
          ? (isUSA ? 'Product updated' : 'Producto actualizado') 
          : (isUSA ? 'Product added' : 'Producto agregado'));
        setShowProductDialog(false);
        setEditingProduct(null);
        resetProductForm();
        fetchData();
      } else if (res.status === 403) {
        // User needs Pro plan to add products to store
        setShowProductDialog(false);
        setPaywallFeature(isUSA ? 'add products to your store' : 'agregar productos a tu tienda');
        setShowPaywall(true);
      } else {
        toast.error(data.detail || (isUSA ? 'Error saving product' : 'Error al guardar producto'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error saving product' : 'Error al guardar producto');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(isUSA ? 'Are you sure you want to delete this product?' : '¿Estás seguro de eliminar este producto?')) return;

    try {
      const res = await fetch(`${API_URL}/api/stores/my/products/${productId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success(isUSA ? 'Product deleted' : 'Producto eliminado');
        fetchData();
      } else {
        toast.error(isUSA ? 'Error deleting product' : 'Error al eliminar producto');
      }
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleUpdateLeadStatus = async (leadId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/stores/my/leads/${leadId}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Estado actualizado');
        fetchData();
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/stores/my/orders/${orderId}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success(isUSA ? 'Order status updated' : 'Estado del pedido actualizado');
        fetchData();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error updating order status' : 'Error al actualizar estado del pedido');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      name_en: '',
      category: '',
      subcategory: '',
      description: '',
      description_en: '',
      price: '',
      currency: isUSA ? 'USD' : 'MXN',
      unit: 'Mt',
      hide_price: false,
      images: [],
      moq: '',
      stock: '',
      tags: '',
      publish_region: 'MX',
      variants: { color: [], talla: [], modelo: [] }
    });
    setNewVariant({ type: 'color', value: '' });
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      name_en: product.name_en || '',
      category: product.category,
      subcategory: product.subcategory || '',
      description: product.description,
      description_en: product.description_en || '',
      price: product.price?.toString() || '',
      currency: product.currency || 'MXN',
      unit: product.unit || (product.category === 'telas' ? 'Mt' : 'pzas'),
      hide_price: product.hide_price || false,
      images: product.images || [],
      moq: product.moq?.toString() || '',
      stock: product.stock?.toString() || '',
      tags: product.tags?.join(', ') || '',
      publish_region: product.publish_region || 'MX',
      variants: product.variants || { color: [], talla: [], modelo: [] }
    });
    setNewVariant({ type: 'color', value: '' });
    setShowProductDialog(true);
  };

  const plan = user?.subscription_plan || 'free';
  const planLimits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
  const canAddMore = planLimits.max_products === -1 || products.length < planLimits.max_products;
  const isPro = plan === 'pro';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // No store yet - show create
  if (!store) {
    return (
      <div className="max-w-3xl mx-auto">
        <Card className="border-2 border-dashed border-slate-300">
          <CardContent className="p-8 text-center">
            <Store className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {isUSA ? 'Create Your Online Store' : 'Crea tu Tienda en Línea'}
            </h2>
            <p className="text-slate-600 mb-6">
              {isUSA 
                ? 'Showcase your products to thousands of potential buyers in the textile industry.'
                : 'Muestra tus productos a miles de compradores potenciales en la industria textil.'}
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-6 text-left">
              <div className="p-4 bg-slate-50 rounded-lg">
                <Package className="w-6 h-6 text-emerald-600 mb-2" />
                <p className="text-sm font-medium">{isUSA ? 'Product Catalog' : 'Catálogo de Productos'}</p>
                <p className="text-xs text-slate-500">{isUSA ? 'Upload photos and details' : 'Sube fotos y detalles'}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <Eye className="w-6 h-6 text-emerald-600 mb-2" />
                <p className="text-sm font-medium">{isUSA ? 'Visibility' : 'Visibilidad'}</p>
                <p className="text-xs text-slate-500">{isUSA ? 'Appear in searches' : 'Aparece en búsquedas'}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <ShoppingBag className="w-6 h-6 text-emerald-600 mb-2" />
                <p className="text-sm font-medium">{isUSA ? 'Generate Leads' : 'Genera Leads'}</p>
                <p className="text-xs text-slate-500">{isUSA ? 'Receive inquiries' : 'Recibe solicitudes'}</p>
              </div>
            </div>

            <Button onClick={() => setShowCreateStore(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              {isUSA ? 'Create my Store' : 'Crear mi Tienda'}
            </Button>
          </CardContent>
        </Card>

        {/* Create Store Dialog */}
        <Dialog open={showCreateStore} onOpenChange={setShowCreateStore}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{isUSA ? 'Create Online Store' : 'Crear Tienda en Línea'}</DialogTitle>
              <DialogDescription>{isUSA ? 'Set up your store to start selling' : 'Configura tu tienda para empezar a vender'}</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{isUSA ? 'Store name *' : 'Nombre de la tienda *'}</Label>
                <Input 
                  value={storeForm.store_name}
                  onChange={(e) => setStoreForm({...storeForm, store_name: e.target.value})}
                  placeholder={isUSA ? 'My Fabric Store' : 'Mi Tienda de Telas'}
                />
              </div>

              <div className="space-y-2">
                <Label>{isUSA ? 'Main category *' : 'Categoría principal *'}</Label>
                <Select 
                  value={storeForm.category}
                  onValueChange={(v) => setStoreForm({...storeForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isUSA ? 'Select...' : 'Selecciona...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{isUSA ? 'Description' : 'Descripción'}</Label>
                <Textarea 
                  value={storeForm.description}
                  onChange={(e) => setStoreForm({...storeForm, description: e.target.value})}
                  placeholder={isUSA ? 'Describe your store and products...' : 'Describe tu tienda y productos...'}
                  rows={3}
                />
              </div>

              <ImageUploader 
                value={storeForm.logo_url}
                onChange={(url) => setStoreForm({...storeForm, logo_url: url})}
                label={isUSA ? 'Store logo' : 'Logo de la tienda'}
                aspectRatio="aspect-square w-32"
              />

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox"
                    id="accept-terms"
                    checked={storeForm.accepted_terms}
                    onChange={(e) => setStoreForm({...storeForm, accepted_terms: e.target.checked})}
                    className="mt-1"
                  />
                  <label htmlFor="accept-terms" className="text-sm text-amber-800">
                    {isUSA ? 'I accept the ' : 'Acepto los '}
                    <button 
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-amber-600 underline hover:text-amber-700"
                    >
                      {isUSA ? 'terms and conditions' : 'términos y condiciones'}
                    </button>
                    {isUSA ? ' of the online store.' : ' de la tienda en línea.'}
                  </label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateStore(false)}>{isUSA ? 'Cancel' : 'Cancelar'}</Button>
              <Button onClick={handleCreateStore} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700">
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Create Store' : 'Crear Tienda')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Terms Dialog */}
        <Dialog open={showTerms} onOpenChange={setShowTerms}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isUSA ? 'Terms and Conditions' : 'Términos y Condiciones'}</DialogTitle>
            </DialogHeader>
            <div className="prose prose-sm max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{terms}</pre>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowTerms(false)}>{isUSA ? 'Close' : 'Cerrar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Store exists - show management
  return (
    <div className="space-y-6">
      {/* Store Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          {store.logo_url ? (
            <img src={store.logo_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
          ) : (
            <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Store className="w-8 h-8 text-emerald-600" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{store.store_name}</h1>
            <p className="text-slate-600 text-sm">{store.description || (isUSA ? 'No description' : 'Sin descripción')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowCustomizeDialog(true)}>
            <Settings className="w-4 h-4 mr-2" />
            {isUSA ? 'Customize' : 'Personalizar'}
          </Button>
          <Link to={`/tienda/${store.slug}`} target="_blank">
            <Button variant="outline" size="sm">
              <ExternalLink className="w-4 h-4 mr-2" />
              {isUSA ? 'View store' : 'Ver tienda'}
            </Button>
          </Link>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleShareStore}
            className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
            {isUSA ? 'Share link' : 'Compartir enlace'}
          </Button>
          <Badge className={plan === 'pro' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>
            {plan === 'pro' ? 'PRO' : 'Free'}
          </Badge>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Package className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{products.length}</p>
            <p className="text-xs text-slate-500">
              {isUSA ? 'Products' : 'Productos'} {planLimits.max_products !== -1 && `/ ${planLimits.max_products}`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Eye className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{store.total_views || 0}</p>
            <p className="text-xs text-slate-500">{isUSA ? 'Visits' : 'Visitas'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <ShoppingBag className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{leads.filter(l => l.status === 'nueva').length}</p>
            <p className="text-xs text-slate-500">{isUSA ? 'New inquiries' : 'Solicitudes nuevas'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            {planLimits.price_visible ? (
              <CheckCircle2 className="w-6 h-6 text-green-600 mx-auto mb-2" />
            ) : (
              <AlertCircle className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            )}
            <p className="text-sm font-medium">{planLimits.price_visible 
              ? (isUSA ? 'Visible prices' : 'Precios visibles') 
              : (isUSA ? 'Hidden prices' : 'Precios ocultos')}</p>
            <p className="text-xs text-slate-500">{!planLimits.price_visible && (isUSA ? 'Upgrade to PRO' : 'Mejora a PRO')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Plan limit warning */}
      {!canAddMore && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <Crown className="w-8 h-8 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">{isUSA ? 'Product limit reached' : 'Límite de productos alcanzado'}</p>
              <p className="text-sm text-amber-700">
                {isUSA 
                  ? `Your ${plan === 'pro' ? 'PRO' : 'Free'} plan allows a maximum of ${planLimits.max_products} products.`
                  : `Tu plan ${plan === 'pro' ? 'PRO' : 'Free'} permite máximo ${planLimits.max_products} productos.`}
              </p>
            </div>
            <Link to="/dashboard/subscription">
              <Button className="bg-amber-600 hover:bg-amber-700">{isUSA ? 'Upgrade plan' : 'Mejorar plan'}</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('products')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${activeTab === 'products' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-600'}`}
        >
          {isUSA ? 'Products' : 'Productos'} ({products.length})
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${activeTab === 'orders' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-600'}`}
        >
          {isUSA ? 'Orders' : 'Pedidos'} ({storeOrders.length})
        </button>
        <button
          onClick={() => setActiveTab('leads')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${activeTab === 'leads' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-600'}`}
        >
          {isUSA ? 'Inquiries' : 'Solicitudes'} ({leads.length})
        </button>
        <button
          onClick={() => setActiveTab('metrics')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${activeTab === 'metrics' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-600'}`}
        >
          {isUSA ? 'Analytics' : 'Métricas'}
        </button>
      </div>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div>
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold">{isUSA ? 'My Products' : 'Mis Productos'}</h2>
            <div className="flex items-center gap-2">
              {/* Download Template Button */}
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  window.open(`${API_URL}/api/stores/my/products/template/excel`, '_blank');
                }}
                title={isUSA ? 'Download Excel template' : 'Descargar plantilla Excel'}
              >
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{isUSA ? 'Template' : 'Plantilla'}</span>
              </Button>
              
              {/* Upload Excel Button */}
              <Button 
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('excel-upload').click()}
                disabled={!isPro}
                title={isPro ? (isUSA ? 'Import from Excel' : 'Importar desde Excel') : (isUSA ? 'PRO feature' : 'Función PRO')}
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{isUSA ? 'Import' : 'Importar'}</span>
              </Button>
              <input 
                id="excel-upload"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  
                  // Validate file type
                  if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                    toast.error(isUSA ? 'Please select an Excel file (.xlsx)' : 'Selecciona un archivo Excel (.xlsx)');
                    return;
                  }
                  
                  const formData = new FormData();
                  formData.append('file', file);
                  
                  setLoading(true);
                  try {
                    const res = await fetch(`${API_URL}/api/stores/my/products/import/excel`, {
                      method: 'POST',
                      credentials: 'include',
                      body: formData
                    });
                    
                    if (!res.ok) {
                      let errorMessage = 'Error al importar';
                      try {
                        const errorData = await res.json();
                        errorMessage = errorData.detail || errorMessage;
                      } catch (e) {
                        errorMessage = `Error ${res.status}: ${res.statusText}`;
                      }
                      toast.error(errorMessage);
                      return;
                    }
                    
                    const data = await res.json();
                    // Show detailed message with errors if any
                    if (data.results?.errors?.length > 0) {
                      toast.warning(`${data.message}. Errores: ${data.results.errors.slice(0, 3).join(', ')}${data.results.errors.length > 3 ? '...' : ''}`, { duration: 8000 });
                    } else {
                      toast.success(data.message);
                    }
                    fetchProducts();
                  } catch (err) {
                    console.error('Import error:', err);
                    toast.error(`Error de conexión: ${err.message || 'Intenta de nuevo'}`);
                  } finally {
                    setLoading(true);
                    // Small delay to let server finish processing
                    setTimeout(() => {
                      setLoading(false);
                      e.target.value = '';
                    }, 500);
                  }
                }}
              />
              
              <Button 
                onClick={() => {
                  resetProductForm();
                  setEditingProduct(null);
                  setShowProductDialog(true);
                }}
                disabled={!canAddMore}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                {isUSA ? 'Add product' : 'Agregar producto'}
              </Button>
            </div>
          </div>

          {products.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">{isUSA ? "You don't have products yet" : 'Aún no tienes productos'}</p>
                <p className="text-sm text-slate-500">{isUSA ? 'Add your first product to start' : 'Agrega tu primer producto para empezar'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product) => (
                <Card key={product.product_id} className="overflow-hidden">
                  <div className="aspect-video bg-slate-100 relative">
                    {product.images?.[0] ? (
                      <img src={getFirstImage(product.images)} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    {!product.is_active && (
                      <Badge className="absolute top-2 left-2 bg-red-100 text-red-700">{isUSA ? 'Inactive' : 'Inactivo'}</Badge>
                    )}
                    {/* Market Region Indicator */}
                    <div className="absolute top-2 right-2">
                      {product.publish_region === 'BOTH' ? (
                        <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">🌎</span>
                      ) : product.publish_region === 'US' ? (
                        <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">🇺🇸</span>
                      ) : (
                        <span className="bg-emerald-600 text-white text-xs px-2 py-1 rounded-full font-medium">🇲🇽</span>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-slate-900 truncate">{product.name}</h3>
                    <p className="text-sm text-slate-500">{product.category}</p>
                    {product.price && !product.hide_price && planLimits.price_visible && (
                      <p className="text-emerald-600 font-semibold mt-1">${product.price.toLocaleString()} {product.currency || 'MXN'}/{product.unit || (product.category === 'telas' ? 'Mt' : (isUSA ? 'Unit' : 'pza'))}</p>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-xs text-slate-500">{product.views} {isUSA ? 'views' : 'vistas'}</span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditProduct(product)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteProduct(product.product_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div>
          <h2 className="text-lg font-semibold mb-4">{isUSA ? 'Received Orders' : 'Pedidos Recibidos'}</h2>
          
          {storeOrders.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">{isUSA ? "You don't have orders yet" : 'No tienes pedidos aún'}</p>
                <p className="text-sm text-slate-500 mt-2">
                  {isUSA 
                    ? 'When customers place orders from your store, they will appear here' 
                    : 'Cuando los clientes hagan pedidos desde tu tienda, aparecerán aquí'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {storeOrders.map((order) => (
                <Card key={order.order_id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row justify-between gap-4">
                      {/* Order Header */}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <span className="text-sm font-mono text-slate-500">{order.order_id}</span>
                          <Badge className={
                            order.status === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                            order.status === 'confirmado' ? 'bg-blue-100 text-blue-700' :
                            order.status === 'en_produccion' ? 'bg-purple-100 text-purple-700' :
                            order.status === 'enviado' ? 'bg-indigo-100 text-indigo-700' :
                            order.status === 'entregado' ? 'bg-emerald-100 text-emerald-700' :
                            order.status === 'cancelado' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {order.status === 'pendiente' ? (isUSA ? 'Pending' : 'Pendiente') :
                             order.status === 'confirmado' ? (isUSA ? 'Confirmed' : 'Confirmado') :
                             order.status === 'en_produccion' ? (isUSA ? 'In Production' : 'En Producción') :
                             order.status === 'enviado' ? (isUSA ? 'Shipped' : 'Enviado') :
                             order.status === 'entregado' ? (isUSA ? 'Delivered' : 'Entregado') :
                             order.status === 'cancelado' ? (isUSA ? 'Cancelled' : 'Cancelado') :
                             order.status}
                          </Badge>
                          <span className="text-sm text-slate-500">
                            {new Date(order.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
                          </span>
                        </div>

                        {/* Customer Info */}
                        <div className="mb-3 p-3 bg-slate-50 rounded-lg">
                          <p className="font-semibold">{order.customer_name}</p>
                          <p className="text-sm text-slate-600">{order.customer_email}</p>
                          {order.customer_phone && <p className="text-sm text-slate-600">{order.customer_phone}</p>}
                          {order.customer_company && <p className="text-sm text-slate-500">{order.customer_company}</p>}
                          <p className="text-sm text-slate-600 mt-1">
                            📍 {order.shipping_address}, {order.shipping_city}, {order.shipping_state}
                          </p>
                        </div>

                        {/* Order Items */}
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-slate-700">{isUSA ? 'Products:' : 'Productos:'}</p>
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm border-b pb-2">
                              <div>
                                <span className="font-medium">{item.product_name}</span>
                                {item.variant_name && <span className="text-slate-500"> ({item.variant_name})</span>}
                              </div>
                              <div className="text-right">
                                <span>{item.quantity} {item.unit}</span>
                                {item.unit_price && (
                                  <span className="text-emerald-600 ml-2">${item.unit_price?.toLocaleString()}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Subtotal */}
                        {order.subtotal > 0 && (
                          <div className="mt-3 text-right">
                            <span className="font-semibold text-lg text-emerald-600">
                              Total: ${order.subtotal?.toLocaleString()} MXN
                            </span>
                          </div>
                        )}

                        {/* Notes */}
                        {order.notes && (
                          <div className="mt-3 p-2 bg-amber-50 rounded text-sm">
                            <span className="font-medium">{isUSA ? 'Notes:' : 'Notas:'}</span> {order.notes}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex lg:flex-col gap-2 flex-wrap">
                        {order.status === 'pendiente' && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleUpdateOrderStatus(order.order_id, 'confirmado')}
                            >
                              {isUSA ? 'Confirm' : 'Confirmar'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleUpdateOrderStatus(order.order_id, 'cancelado')}
                            >
                              {isUSA ? 'Cancel' : 'Cancelar'}
                            </Button>
                          </>
                        )}
                        {order.status === 'confirmado' && (
                          <Button 
                            size="sm" 
                            onClick={() => handleUpdateOrderStatus(order.order_id, 'en_produccion')}
                          >
                            {isUSA ? 'Start Production' : 'Iniciar Producción'}
                          </Button>
                        )}
                        {order.status === 'en_produccion' && (
                          <Button 
                            size="sm" 
                            onClick={() => handleUpdateOrderStatus(order.order_id, 'enviado')}
                          >
                            {isUSA ? 'Mark Shipped' : 'Marcar Enviado'}
                          </Button>
                        )}
                        {order.status === 'enviado' && (
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleUpdateOrderStatus(order.order_id, 'entregado')}
                          >
                            {isUSA ? 'Mark Delivered' : 'Marcar Entregado'}
                          </Button>
                        )}
                        <a 
                          href={`mailto:${order.customer_email}?subject=${encodeURIComponent(`Pedido ${order.order_id} - ${store?.store_name}`)}`}
                          className="inline-flex"
                        >
                          <Button size="sm" variant="outline">
                            <Mail className="w-4 h-4 mr-1" />
                            {isUSA ? 'Email' : 'Correo'}
                          </Button>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <div>
          <h2 className="text-lg font-semibold mb-4">{isUSA ? 'Received Inquiries' : 'Solicitudes Recibidas'}</h2>
          
          {leads.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">{isUSA ? "You don't have inquiries yet" : 'No tienes solicitudes aún'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => (
                <Card key={lead.lead_id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={
                            lead.lead_type === 'cotizacion' ? 'bg-blue-100 text-blue-700' :
                            lead.lead_type === 'muestra' ? 'bg-purple-100 text-purple-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {lead.lead_type === 'cotizacion' ? 'Cotización' : lead.lead_type === 'muestra' ? 'Muestra' : 'Contacto'}
                          </Badge>
                          <Badge variant="outline">{lead.status}</Badge>
                        </div>
                        <p className="font-medium">{lead.contact_name}</p>
                        <p className="text-sm text-slate-600">{lead.contact_email}</p>
                        {lead.product_name && <p className="text-sm text-emerald-600">Producto: {lead.product_name}</p>}
                        <p className="text-sm text-slate-700 mt-2">{lead.message}</p>
                      </div>
                      <div className="flex sm:flex-col gap-2">
                        {lead.status === 'nueva' && (
                          <Button size="sm" onClick={() => handleUpdateLeadStatus(lead.lead_id, 'contactado')}>
                            Contactado
                          </Button>
                        )}
                        {lead.status === 'contactado' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateLeadStatus(lead.lead_id, 'cerrado')}>
                            {isUSA ? 'Close' : 'Cerrar'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Metrics Tab */}
      {activeTab === 'metrics' && (
        <div className="py-4">
          <StoreMetrics />
        </div>
      )}

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct 
              ? (isUSA ? 'Edit Product' : 'Editar Producto') 
              : (isUSA ? 'Add Product' : 'Agregar Producto')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <MultiImageUploader 
              images={productForm.images}
              onChange={(imgs) => setProductForm({...productForm, images: imgs})}
            />

            <div className="space-y-2">
              <Label>{isUSA ? 'Product name (Spanish) *' : 'Nombre del producto (Español) *'}</Label>
              <Input 
                value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                placeholder={isUSA ? 'Ex: 100% Cotton Fabric' : 'Ej: Tela de algodón 100%'}
              />
            </div>

            {/* Publish Region Selector */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
              <Label className="font-medium">{isUSA ? 'Where do you want to sell?' : '¿Dónde quieres vender?'}</Label>
              <div className="flex flex-wrap gap-3">
                <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${productForm.publish_region === 'MX' ? 'bg-emerald-100 border-emerald-500 text-emerald-800' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="publish_region"
                    value="MX"
                    checked={productForm.publish_region === 'MX'}
                    onChange={(e) => setProductForm({...productForm, publish_region: e.target.value})}
                    className="sr-only"
                  />
                  <span className="text-base">🇲🇽</span>
                  <span className="text-sm font-medium">{isUSA ? 'Mexico' : 'México'}</span>
                </label>
                <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${productForm.publish_region === 'US' ? 'bg-blue-100 border-blue-500 text-blue-800' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="publish_region"
                    value="US"
                    checked={productForm.publish_region === 'US'}
                    onChange={(e) => setProductForm({...productForm, publish_region: e.target.value})}
                    className="sr-only"
                  />
                  <span className="text-base">🇺🇸</span>
                  <span className="text-sm font-medium">USA</span>
                </label>
                <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${productForm.publish_region === 'BOTH' ? 'bg-purple-100 border-purple-500 text-purple-800' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="publish_region"
                    value="BOTH"
                    checked={productForm.publish_region === 'BOTH'}
                    onChange={(e) => setProductForm({...productForm, publish_region: e.target.value})}
                    className="sr-only"
                  />
                  <span className="text-base">🌎</span>
                  <span className="text-sm font-medium">{isUSA ? 'Both' : 'Ambos'}</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isUSA ? 'Category *' : 'Categoría *'}</Label>
                <Select 
                  value={productForm.category}
                  onValueChange={(v) => setProductForm({...productForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isUSA ? 'Select...' : 'Selecciona...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Subcategory' : 'Subcategoría'}</Label>
                <Input 
                  value={productForm.subcategory}
                  onChange={(e) => setProductForm({...productForm, subcategory: e.target.value})}
                  placeholder={isUSA ? 'Ex: Cotton' : 'Ej: Algodón'}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isUSA ? 'Description *' : 'Descripción *'}</Label>
              <Textarea 
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                placeholder={isUSA ? 'Describe the product...' : 'Describe el producto...'}
                rows={3}
              />
            </div>

            {/* English Translation Section - Only shows when selling to US or BOTH */}
            {showEnglishFields && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Languages className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-900">{isUSA ? 'English Translation' : 'Traducción al Inglés'}</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleTranslate}
                    disabled={translating || (!productForm.name && !productForm.description)}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    {translating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isUSA ? 'Translating...' : 'Traduciendo...'}</>
                    ) : (
                      <><Languages className="w-4 h-4 mr-2" /> {isUSA ? 'Auto-translate' : 'Traducir'}</>
                    )}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-blue-800">{isUSA ? 'Name in English' : 'Nombre en inglés'}</Label>
                  <Input
                    value={productForm.name_en}
                    onChange={(e) => setProductForm({...productForm, name_en: e.target.value})}
                    placeholder="Product name in English"
                    className="bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-blue-800">{isUSA ? 'Description in English' : 'Descripción en inglés'}</Label>
                  <Textarea
                    value={productForm.description_en}
                    onChange={(e) => setProductForm({...productForm, description_en: e.target.value})}
                    placeholder="Product description in English..."
                    rows={2}
                    className="bg-white"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>{isUSA ? 'Price' : 'Precio'}</Label>
                <Input 
                  type="number"
                  value={productForm.price}
                  onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Currency' : 'Moneda'}</Label>
                <Select value={productForm.currency} onValueChange={(v) => setProductForm({...productForm, currency: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">🇲🇽 MXN</SelectItem>
                    <SelectItem value="USD">🇺🇸 USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Unit' : 'Unidad'}</Label>
                <Select value={productForm.unit} onValueChange={(v) => setProductForm({...productForm, unit: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getUnitOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={productForm.hide_price}
                    onChange={(e) => setProductForm({...productForm, hide_price: e.target.checked})}
                  />
                  <span className="text-sm">{isUSA ? 'Hide' : 'Ocultar'}</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>MOQ</Label>
                <Input 
                  type="number"
                  value={productForm.moq}
                  onChange={(e) => setProductForm({...productForm, moq: e.target.value})}
                  placeholder="100"
                />
              </div>
              <div className="space-y-2">
                <Label>Stock</Label>
                <Input 
                  type="number"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isUSA ? 'Tags (comma separated)' : 'Tags (separados por coma)'}</Label>
              <Input 
                value={productForm.tags}
                onChange={(e) => setProductForm({...productForm, tags: e.target.value})}
                placeholder={isUSA ? 'cotton, fabric, blue' : 'algodón, tela, azul'}
              />
            </div>

            {/* Variants Section */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">{isUSA ? 'Variants (Colors, Sizes, Models)' : 'Variantes (Colores, Tallas, Modelos)'}</Label>
              <p className="text-xs text-slate-500">
                {isUSA ? 'Add options that customers can choose when ordering' : 'Agrega opciones que los clientes pueden elegir al ordenar'}
              </p>
              
              {/* Add new variant */}
              <div className="flex gap-2">
                <Select value={newVariant.type} onValueChange={(v) => setNewVariant({...newVariant, type: v})}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="color">{isUSA ? 'Color' : 'Color'}</SelectItem>
                    <SelectItem value="talla">{isUSA ? 'Size' : 'Talla'}</SelectItem>
                    <SelectItem value="modelo">{isUSA ? 'Model' : 'Modelo'}</SelectItem>
                  </SelectContent>
                </Select>
                <Input 
                  value={newVariant.value}
                  onChange={(e) => setNewVariant({...newVariant, value: e.target.value})}
                  placeholder={isUSA ? 'e.g., Red, XL, Classic' : 'ej: Rojo, XL, Clásico'}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newVariant.value.trim()) {
                      e.preventDefault();
                      const type = newVariant.type;
                      const value = newVariant.value.trim();
                      if (!productForm.variants[type].includes(value)) {
                        setProductForm({
                          ...productForm,
                          variants: {
                            ...productForm.variants,
                            [type]: [...productForm.variants[type], value]
                          }
                        });
                      }
                      setNewVariant({...newVariant, value: ''});
                    }
                  }}
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (newVariant.value.trim()) {
                      const type = newVariant.type;
                      const value = newVariant.value.trim();
                      if (!productForm.variants[type].includes(value)) {
                        setProductForm({
                          ...productForm,
                          variants: {
                            ...productForm.variants,
                            [type]: [...productForm.variants[type], value]
                          }
                        });
                      }
                      setNewVariant({...newVariant, value: ''});
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Display existing variants */}
              <div className="space-y-2">
                {productForm.variants.color?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-slate-500 w-16">{isUSA ? 'Colors:' : 'Colores:'}</span>
                    {productForm.variants.color.map((c, i) => (
                      <Badge key={i} variant="secondary" className="flex items-center gap-1">
                        {c}
                        <button
                          type="button"
                          onClick={() => setProductForm({
                            ...productForm,
                            variants: {
                              ...productForm.variants,
                              color: productForm.variants.color.filter((_, idx) => idx !== i)
                            }
                          })}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {productForm.variants.talla?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-slate-500 w-16">{isUSA ? 'Sizes:' : 'Tallas:'}</span>
                    {productForm.variants.talla.map((t, i) => (
                      <Badge key={i} variant="secondary" className="flex items-center gap-1">
                        {t}
                        <button
                          type="button"
                          onClick={() => setProductForm({
                            ...productForm,
                            variants: {
                              ...productForm.variants,
                              talla: productForm.variants.talla.filter((_, idx) => idx !== i)
                            }
                          })}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {productForm.variants.modelo?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-slate-500 w-16">{isUSA ? 'Models:' : 'Modelos:'}</span>
                    {productForm.variants.modelo.map((m, i) => (
                      <Badge key={i} variant="secondary" className="flex items-center gap-1">
                        {m}
                        <button
                          type="button"
                          onClick={() => setProductForm({
                            ...productForm,
                            variants: {
                              ...productForm.variants,
                              modelo: productForm.variants.modelo.filter((_, idx) => idx !== i)
                            }
                          })}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>{isUSA ? 'Cancel' : 'Cancelar'}</Button>
            <Button onClick={handleSaveProduct} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Save' : 'Guardar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customize Store Dialog */}
      <Dialog open={showCustomizeDialog} onOpenChange={setShowCustomizeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isUSA ? 'Customize Store' : 'Personalizar Tienda'}</DialogTitle>
            <DialogDescription>{isUSA ? 'Configure the appearance and contact details of your store' : 'Configura la apariencia y datos de contacto de tu tienda'}</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="design">{isUSA ? 'Design' : 'Diseño'}</TabsTrigger>
              <TabsTrigger value="contact">{isUSA ? 'Contact' : 'Contacto'}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <ImageUploader 
                  value={customizeForm.logo_url}
                  onChange={(url) => setCustomizeForm({...customizeForm, logo_url: url})}
                  label="Logo"
                  aspectRatio="aspect-square"
                />
                <ImageUploader 
                  value={customizeForm.cover_url}
                  onChange={(url) => setCustomizeForm({...customizeForm, cover_url: url})}
                  label={isUSA ? 'Cover' : 'Portada'}
                  aspectRatio="aspect-video"
                />
              </div>

              <div className="space-y-2">
                <Label>{isUSA ? 'Store name' : 'Nombre de la tienda'}</Label>
                <Input 
                  value={customizeForm.store_name}
                  onChange={(e) => setCustomizeForm({...customizeForm, store_name: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>{isUSA ? 'Custom URL' : 'URL personalizada'}</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">texhub.com.mx/tienda/</span>
                  <Input 
                    value={customizeForm.slug}
                    onChange={(e) => setCustomizeForm({...customizeForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{isUSA ? 'Description' : 'Descripción'}</Label>
                <Textarea 
                  value={customizeForm.description}
                  onChange={(e) => setCustomizeForm({...customizeForm, description: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>{isUSA ? 'Promotional banner text' : 'Texto de banner promocional'}</Label>
                <Input 
                  value={customizeForm.banner_text}
                  onChange={(e) => setCustomizeForm({...customizeForm, banner_text: e.target.value})}
                  placeholder={isUSA ? 'Ex: Free shipping on orders over $500!' : 'Ej: ¡Envío gratis en pedidos mayores a $5,000!'}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="design" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  {isUSA ? 'Store colors' : 'Colores de la tienda'}
                </Label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => setCustomizeForm({...customizeForm, primary_color: preset.primary, secondary_color: preset.secondary})}
                      className={`p-2 rounded-lg border-2 transition-all ${customizeForm.primary_color === preset.primary ? 'border-slate-900 scale-105' : 'border-transparent hover:border-slate-300'}`}
                    >
                      <div className="w-full aspect-square rounded-full" style={{ background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})` }} />
                      <p className="text-[10px] text-center mt-1 text-slate-600">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isUSA ? 'Primary color' : 'Color primario'}</Label>
                  <div className="flex gap-2">
                    <input 
                      type="color"
                      value={customizeForm.primary_color}
                      onChange={(e) => setCustomizeForm({...customizeForm, primary_color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input 
                      value={customizeForm.primary_color}
                      onChange={(e) => setCustomizeForm({...customizeForm, primary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{isUSA ? 'Secondary color' : 'Color secundario'}</Label>
                  <div className="flex gap-2">
                    <input 
                      type="color"
                      value={customizeForm.secondary_color}
                      onChange={(e) => setCustomizeForm({...customizeForm, secondary_color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input 
                      value={customizeForm.secondary_color}
                      onChange={(e) => setCustomizeForm({...customizeForm, secondary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-4 p-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${customizeForm.primary_color}, ${customizeForm.secondary_color})` }}>
                <p className="text-white text-center font-medium">{isUSA ? 'Store preview' : 'Vista previa de tu tienda'}</p>
              </div>
            </TabsContent>
            
            <TabsContent value="contact" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {isUSA ? 'Contact email' : 'Email de contacto'}
                </Label>
                <Input 
                  type="email"
                  value={customizeForm.contact_email}
                  onChange={(e) => setCustomizeForm({...customizeForm, contact_email: e.target.value})}
                  placeholder={isUSA ? 'sales@mycompany.com' : 'ventas@miempresa.com'}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {isUSA ? 'Phone' : 'Teléfono'}
                </Label>
                <Input 
                  value={customizeForm.contact_phone}
                  onChange={(e) => setCustomizeForm({...customizeForm, contact_phone: e.target.value})}
                  placeholder="55 1234 5678"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  WhatsApp
                </Label>
                <Input 
                  value={customizeForm.contact_whatsapp}
                  onChange={(e) => setCustomizeForm({...customizeForm, contact_whatsapp: e.target.value})}
                  placeholder="521234567890"
                />
              </div>

              <hr />

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Facebook className="w-4 h-4" />
                  Facebook
                </Label>
                <Input 
                  value={customizeForm.social_facebook}
                  onChange={(e) => setCustomizeForm({...customizeForm, social_facebook: e.target.value})}
                  placeholder="https://facebook.com/mitienda"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Instagram className="w-4 h-4" />
                  Instagram
                </Label>
                <Input 
                  value={customizeForm.social_instagram}
                  onChange={(e) => setCustomizeForm({...customizeForm, social_instagram: e.target.value})}
                  placeholder="https://instagram.com/mitienda"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {isUSA ? 'Website' : 'Sitio web'}
                </Label>
                <Input 
                  value={customizeForm.social_website}
                  onChange={(e) => setCustomizeForm({...customizeForm, social_website: e.target.value})}
                  placeholder="https://www.mitienda.com"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomizeDialog(false)}>{isUSA ? 'Cancel' : 'Cancelar'}</Button>
            <Button onClick={handleSaveCustomization} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Save changes' : 'Guardar cambios')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pro Paywall Modal */}
      <ProPaywall 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        feature={paywallFeature}
      />
    </div>
  );
};

export default MyStorePage;
